//lib
import { Link } from "react-router-dom";
import classNames from "classnames";
import { getDate, getDay, getMonth, parse } from "date-fns";
import { TextField, IconButton, Typography } from "@mui/material";
//src
import { DeleteIcon } from "../../../utils/svg-components";
import { MONTHS_ARRAY, daysArray } from "../../../utils/contants";
import {
  isSomething,
  roundToNearestQuarterHour,
  getCompanyNameById,
} from "../../../utils/utils";
import SelectField from "../../../components/form-fields/select-field/select-field";
import { getEnabledRolesListForDeliverable } from "./time-sheet.utils";
import AutoCompleteField from "../../../components/form-fields/auto-complte-new/auto-complete-field";

const getTotalsBackgroundColor = (hours) => {
  if (hours >= 0 && hours <= 4) return "#E87373"; // Red: 0-4 hours tracked
  if (hours >= 4.01 && hours <= 6.99) return "#FF950A"; // Orange: 4.01-6 hours tracked
  if (hours >= 7 && hours <= 8) return "#77E250"; // Green: 6.01-8.24 hours tracked
  if (hours >= 8.01) return "#F1D26A"; // Yellow: 8.25 or more hours tracked
};

const getWeeklyTotalsBackgorundColor = (hours) => {
  if (hours >= 0 && hours <= 27) return "#E87373"; // Red: 0-27 hours tracked
  if (hours >= 27.01 && hours <= 34.99) return "#FF950A"; // Orange: 27.01-34 hours tracked
  if (hours >= 35 && hours <= 44) return "#77E250"; // Green: 34.01-44.24 hours tracked
  if (hours >= 44.01) return "#F1D26A"; // Yellow: 44.25 or more hours tracked
};

export const dataColumns = ({
  timeColumnsList,
  tableData,
  starredProjects,
  userToSaveFor,
  timeColumnTotals,
  totalWeekHours,
  sortedProjectsList,
  projectsList,
  resetKey,
  companies,
  toggleStarredProject,
  calculateProjectTotalHours,
  handleUpdateData,
  handleUpdateWeeklyHours,
  handleRemoveProject,
  handleAddProject,
  setTimesheetModalData,
  handleStarUpdate,
}) => {
  const timeColumns = timeColumnsList?.map((timeColumn) => {
    const date = parse(timeColumn, "dd-MM-yyyy", new Date());

    const day = getDay(date);
    const month = getMonth(date);
    const date1 = getDate(date);

    const dayString = daysArray[day];
    const monthName = MONTHS_ARRAY[month];

    const columnTotal =
      timeColumnTotals.find((total) => total.timeColumn === timeColumn)
        ?.totalMinutes || 0;
    const columnTotalHours = (columnTotal / 60).toFixed(2);
    return {
      name: (
        <div className="center" style={{ width: "60px" }}>
          <div className="date-header">
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {dayString}
            </Typography>
            <Typography variant="body2">{` ${monthName} ${date1}`}</Typography>
          </div>
        </div>
      ),
      width: "5%",
      align: "center",
      footerAlign: "center",
      footer: (
        <div
          className={classNames({
            "time-col-footer": true,
          })}
          style={{
            background: getTotalsBackgroundColor(columnTotalHours),
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
            {columnTotalHours}
          </Typography>
        </div>
      ),
      render: (value, rowData, index) => {
        const weeklyHours = rowData?.weeklyHours;
        const project = projectsList?.find((x) => x?.id === rowData?.id);
        const isEditingDisabled = () => {
          const isBillable = !project?.isNonBillable;
          const state = project?.state;
          const isEstimated = isSomething(project?.deliverables);
          const isGrouped = project?.isGrouped;
          if (isGrouped) return true;
          if (!isBillable) {
            if (state === "Committed") return false;
          }
          if (isBillable) {
            if (state === "Active" && isEstimated)
              return !(rowData.deliverable && rowData.role);
            if (state === "Active" && !isEstimated) return true;
            if (state === "Opportunity") return false;
            if (["Complete", "On Hold", "Cancelled"].includes(state))
              return true;
          }
          if (!isBillable) {
            if (["Active", "Opportunity"].includes(state)) return false;
            if (["Complete", "On Hold", "Cancelled"].includes(state))
              return true;
          }
          return !(rowData.deliverable && rowData.role);
        };

        const hourObj = weeklyHours?.find((hour) => hour.date === timeColumn);
        const isdisabled = isEditingDisabled();
        const fieldValue = hourObj?.minutes
          ? (hourObj.minutes / 60).toFixed(2)
          : "0.00";

        return (
          <div className={classNames({ "time-value-class": true })}>
            <div className="edit-option">
              <div
                className={classNames({
                  "disabled-icon": isdisabled,
                  hide: true,
                })}
                onClick={() =>
                  !isdisabled &&
                  setTimesheetModalData({
                    rowData,
                    timeColumn,
                    handleUpdateWeeklyHours,
                  })
                }
              >
                <Typography variant={"body3"}>Add Note</Typography>
              </div>
            </div>
            <TextField
              className="timeInput"
              size="small"
              variant="outlined"
              defaultValue={fieldValue}
              onBlur={(e) => {
                // Convert input value to hours, then to minutes to round to nearest quarter hour
                let inputHours = parseFloat(e.target.value);

                // Validate that hours are not more than 24
                if (inputHours > 24) {
                  alert("You cannot enter more than 24 hours in a day.");
                  e.target.value = "24.00"; // Optionally set the value back to 24.00
                  inputHours = 24;
                }
                let inputMinutes = isNaN(inputHours) ? 0 : inputHours * 60;
                let roundedMinutes = roundToNearestQuarterHour(inputMinutes);

                // Convert rounded minutes back to hours and format to two decimal places
                const formattedValue = (roundedMinutes / 60).toFixed(2);
                e.target.value = formattedValue; // Update the input field display

                const newTotalMinutesForColumn = tableData.reduce(
                  (acc, project) => {
                    const dayData = project.weeklyHours.find(
                      (day) => day.date === timeColumn
                    );
                    return (
                      acc +
                      (project.uniqueId === rowData.uniqueId
                        ? roundedMinutes
                        : dayData
                        ? dayData.minutes
                        : 0)
                    );
                  },
                  0
                );

                // Check if the new total exceeds 1440 minutes (24 hours)
                if (newTotalMinutesForColumn > 1440) {
                  alert("Total hours for the day cannot exceed 24 hours.");
                  e.target.value = fieldValue;
                  return; // Stop the update if it exceeds 24 hours
                }

                // Update the weekly hours with the rounded and formatted value
                handleUpdateWeeklyHours(
                  rowData.uniqueId,
                  timeColumn,
                  roundedMinutes
                );
              }}
              type="number"
              sx={{
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                  {
                    display: "none",
                  },
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
                ".MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              disabled={isEditingDisabled()} // Disable the field if either deliverable or role is not selected
            />
          </div>
        );
      },
    };
  });

  const projectTotalHoursColumn = {
    name: "Total",
    dataKey: "totals",
    width: "5%", // Adjust the width as necessary
    align: "center",
    footer: (
      <div
        className="time-col-footer"
        style={{
          background: getWeeklyTotalsBackgorundColor(totalWeekHours),
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
          {totalWeekHours}
        </Typography>
      </div>
    ),
    footerAlign: "center",
    render: (cellData, rowData) => {
      // Calculate total hours for this row's project
      const totalHours = calculateProjectTotalHours(rowData.weeklyHours);
      return (
        <div className="totals-style">
          <Typography variant="body1">{totalHours}</Typography>
        </div>
      );
    },
  };

  return [
    {
      name: (
        <div className="auto-complete">
          <AutoCompleteField
            key={resetKey}
            clearOnBlur
            name="selectedProject"
            disabled={false}
            defaultValue={null}
            options={sortedProjectsList || []}
            starredProjects={starredProjects}
            showStar={true}
            toggleStar={(projectId) => {
              const isStarred = starredProjects.includes(projectId);
              toggleStarredProject(userToSaveFor, projectId, isStarred).then(
                () => {
                  const updatedProjects = isStarred
                    ? starredProjects.filter((id) => id !== projectId)
                    : [...starredProjects, projectId];
                  handleStarUpdate(updatedProjects); // Update local state
                }
              );
            }}
            onStarUpdate={handleStarUpdate}
            disableClearable
            placeholder={"Search a keyword, client or project to log time."}
            getOptionLabel={(option) =>
              `${option?.id} ${getCompanyNameById(companies, option?.client)} ${option?.projectName}`
            }
            getCustomMenuItem={(option) => {
              return (
                <div>
                  <div>
                    {option?.id} |{" "}
                    {getCompanyNameById(companies, option?.client)}
                  </div>
                  <b>{option?.projectName}</b>
                </div>
              );
            }}
            onChange={(_, val) => {
              handleAddProject(val);
            }}
          />
        </div>
      ),
      dataKey: "projectName",
      width: "15%", // Adjust as needed
      align: "left",

      render: (cellData, rowData) => {
        if (rowData) {
          const companyName =
            companies.find((company) => company.companyID === rowData.client)
              ?.companyName ||
            rowData.clientName ||
            "N/A";

          return (
            <div className="project-cell">
              <div className="icon-style">
                <IconButton
                  disableRipple
                  onClick={() => handleRemoveProject(rowData.uniqueId)}
                >
                  <div className="delete-icon">
                    <DeleteIcon />
                  </div>
                </IconButton>
              </div>
              <div>
                <Typography variant="body3">{`ID: ${rowData.id} | Client: ${companyName}`}</Typography>
                <Typography variant="body1">
                  <Link
                    style={{ color: "inherit" }}
                    to={`/projectsview/${rowData.id}/details?isGroupView=false`}
                    target="_blank"
                  >
                    {rowData?.projectName}
                  </Link>
                </Typography>
              </div>
            </div>
          );
        }

        // Fallback or default render if no match in search results
        return (
          <div className="truncate" title={rowData?.projectName}>
            {rowData?.projectName}
          </div>
        );
      },
    },
    {
      name: "Deliverable",
      dataKey: "deliverable",
      width: "10%",
      align: "center",

      render: (cellData, rowData, i) => {
        return (
          <div
            className={classNames({
              center: true,
              "select-style": true,
              "purple-background": i % 2 === 0,
              "white-background": i % 2 !== 0,
            })}
          >
            <SelectField
              placeholder="Select"
              value={cellData}
              options={rowData?.deliverablesList}
              noBorder
              disabled={rowData?.deliverablesList.length === 0}
              onChange={(event) => {
                handleUpdateData(
                  rowData?.uniqueId,
                  "deliverable",
                  event?.target?.value
                );
              }}
            />
          </div>
        );
      },
    },
    {
      name: "Role",
      dataKey: "role",
      width: "10%",
      align: "left",
      footerAlign: "left",
      footer: (
        <div className="role-footer">
          <Typography variant={"body1"} className="text-style">
            {"Weekly Totals"}
          </Typography>
        </div>
      ),
      render: (cellData, rowData, i) => {
        const findRolesListForNewProject = () => {
          const project = projectsList?.find((x) => x?.id === rowData?.id);
          const projectDeliverables = project?.deliverables;
          const projectResources = project?.resources;
          const selectedDeliverable = projectDeliverables?.find(
            (x) => x?.name === rowData?.deliverable
          );
          const plannerType = project?.plannerType || "Time + Materials";
          const optionsList = getEnabledRolesListForDeliverable(
            selectedDeliverable,
            projectResources,
            plannerType
          );
          return optionsList || [];
        };

        return (
          <div
            className={classNames({
              center: true,
              "select-style": true,
              "purple-background": i % 2 === 0,
              "white-background": i % 2 !== 0,
            })}
          >
            <SelectField
              noBorder
              placeholder="Select"
              value={cellData}
              options={findRolesListForNewProject()}
              disabled={
                !rowData?.deliverable ||
                findRolesListForNewProject()?.length === 0
              }
              onChange={(event) => {
                handleUpdateData(
                  rowData?.uniqueId,
                  "role",
                  event?.target?.value
                );
              }}
            />
          </div>
        );
      },
    },

    ...timeColumns,
    projectTotalHoursColumn,
  ];
};
